/** @typedef ConfigProps
 * @property {string} source_language The source language to translate from
 * @property {string} target_language The target language to translate to
 * @property {string} api_endpoint The translation API endpoint to send requests to
 */

/** @typedef DefaultProps
 * @property {string} source_language The source language to translate from
 * @property {string} target_language The target language to translate to
 * @property {string} api_endpoint The translation API endpoint to send requests to
 */

/** @typedef PassedInProps
 * @property {string} [source_language = "en"] The source language to translate from
 * @property {string} [target_language = "zh-TW"] The target language to translate to
 * @property {string} [api_endpoint] The translation API endpoint to send requests to
 */

/**
 * Creates a new TranslationService instance
 * @param {PassedInProps} passedInConfig
 */
function TranslationService(passedInConfig) {
  /** @type {DefaultProps} */
  let _defaults = {
    source_language: "en",
    target_language: Shopify.locale ?? "zh-TW",
    api_endpoint:
      "https://asia-east2-civic-sunrise-409407.cloudfunctions.net/translate-on-demand",
  };

  /**
   * @type {ConfigProps}
   */
  let _config = { ..._defaults, ...passedInConfig };

  this.translate = async function (inputText) {
    try {
      const response = await fetch(_config.api_endpoint, {
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          source_language: _config.source_language,
          target_language: _config.target_language,
          input: inputText,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        throw new Error(response.statusText);
      }
    } catch (e) {
      console.error(e);
    }
  };
}

/**
 * @typedef ReviewTranslatorPassedInProps
 * @property {TranslationService} translation_service The translation service to use
 * @property {string} [reviews_root_selector = ".stamped-container"] The CSS selector that houses all reviews. MUST be a stable element as it used for mutation observations
 * @property {string} [review_wrapper_selector = ".stamped-review"] The CSS selector for the wrapper element of the review
 * @property {string} [review_text_selector = ".stamped-review-content-body"] The CSS selector for the review text
 * @property {string} [translation_cta_insertion_point = ".stamped-review-footer"] The CSS selector for the element to insert the translation button into
 * @property {"beforebegin" | "afterbegin" | "beforeend" | "afterend"} [translation_cta_insertion_type = "afterbegin"] The type of insertion to use for the translation button. Must be one of "beforebegin", "afterbegin", "beforeend", or "afterend"
 */

/**
 * @typedef ReviewTranslatorDefaults
 * @property {string} reviews_root_selector The CSS selector that houses all reviews. MUST be a stable element as it used for mutation observations
 * @property {string} review_wrapper_selector The CSS selector for the wrapper element of the review
 * @property {string} review_text_selector The CSS selector for the review text
 * @property {string} translation_cta_insertion_point The CSS selector for the element to insert the translation button into
 * @property {"beforebegin" | "afterbegin" | "beforeend" | "afterend"} translation_cta_insertion_type The type of insertion to use for the translation button. Must be one of "beforebegin", "afterbegin", "beforeend", or "afterend"
 */

/**
 * @typedef ReviewTranslatorProps
 * @property {string} reviews_root_selector The CSS selector that houses all reviews. MUST be a stable element as it used for mutation observations
 * @property {string} review_wrapper_selector The CSS selector for the wrapper element of the review
 * @property {string} review_text_selector The CSS selector for the review text
 * @property {TranslationService} translation_service The translation service to use
 * @property {string} translation_cta_insertion_point The CSS selector for the element to insert the translation button into
 * @property {"beforebegin" | "afterbegin" | "beforeend" | "afterend"} translation_cta_insertion_type The type of insertion to use for the translation button. Must be one of "beforebegin", "afterbegin", "beforeend", or "afterend"
 */

/**
 *
 * @param {ReviewTranslatorPassedInProps} config
 */
function ReviewTranslator(config) {
  function defer(method, selector) {
    if (window.jQuery) {
      if (jQuery(selector).length > 0) {
        method();
        return false;
      }
    }
    setTimeout(function () {
      defer(method, selector);
    }, 50);
  }
  /**
   *
   * @param {HTMLElement} element
   */
  function addTranslateButton(element) {
    if (!element) return;

    let insertionPoint = element.querySelector(
      _config.translation_cta_insertion_point,
    );

    if (!insertionPoint) return;

    insertionPoint.insertAdjacentHTML(
      _config.translation_cta_insertion_type,
      `<a href="#" class="translate-review">${window.theme.labels.translate}</a>`,
    );

    let translateCta = insertionPoint.querySelector(".translate-review");

    translateCta.addEventListener("click", function (e) {
      e.preventDefault();

      let $elem = $(this);

      const reviewText = this.closest(
        _config.review_wrapper_selector,
      ).querySelector(_config.review_text_selector);

      if (!reviewText) throw new Error("No review text found");

      if ($elem.data("translated")) {
        let translationVisible = $elem.data("translationVisible");
        $elem.data("translationVisible", !translationVisible);
        $elem.text(
          translationVisible
            ? window.theme.labels.translate
            : window.theme.labels.see_original,
        );
        reviewText.innerText = translationVisible
          ? $elem.data("originalText")
          : $elem.data("translatedText");
        return;
      }

      _config.translation_service
        .translate(reviewText.innerText)
        .then((result) => {
          if (result.status === "success") {
            $elem.data("originalText", reviewText.innerText);
            $elem.data("translated", true);
            $elem.data("translationVisible", true);
            $elem.data("translatedText", result.data.translatedText);

            $elem.text(window.theme.labels.see_original);

            reviewText.innerText = result.data.translatedText;
          }
        });
    });
  }

  /**
   * @type {MutationCallback}
   */
  function handleMutations(mutationsList, observer) {
    for (const mutation of mutationsList) {
      if (mutation.type === "childList" && mutation.addedNodes.length) {
        for (const node of mutation.addedNodes) {
          if (!node.tagName) continue;

          /**
           * @type {HTMLElement}
           */
          let elem = node;

          if (
            !elem.classList.contains(
              _config.review_wrapper_selector.substring(1),
            )
          )
            continue;

          addTranslateButton(elem);
        }
      }
    }
  }

  /**
   * @type {ReviewTranslatorDefaults}
   */
  let _defaults = {
    reviews_root_selector: ".stamped-container",
    review_wrapper_selector: ".stamped-review",
    review_text_selector: ".stamped-review-content-body",
    translation_cta_insertion_point: ".stamped-review-footer",
    translation_cta_insertion_type: "afterbegin",
  };

  if (!config.translation_service) {
    throw new Error("Translation service not provided");
  }

  /**
   * @type {ReviewTranslatorProps}
   */
  let _config = { ..._defaults, ...config };

  defer(function () {
    // Search the reviews root for all reviews
    const reviewsRoot = document.querySelector(_config.reviews_root_selector);

    const reviews = reviewsRoot.querySelectorAll(
      _config.review_wrapper_selector,
    );

    if (!reviews.length) console.warn("No reviews found");

    // Add a translate CTA to any reviews that are already on the page
    for (const review of reviews) {
      addTranslateButton(review);
    }

    // Create mutation observer that will observe the reviews root and add a translate CTA to any new reviews
    const observer = new MutationObserver(handleMutations);

    observer.observe(reviewsRoot, {
      childList: true,
      subtree: true,
    });
  }, _config.reviews_root_selector);
}

window.addEventListener("DOMContentLoaded", function (e) {
  window.theme.translationService = new TranslationService({
    source_language: "en",
    target_language: window.Shopify.locale,
  });

  window.theme.reviewTranslator = new ReviewTranslator({
    translation_service: window.theme.translationService,
  });
});
